import Phaser from 'phaser';
import { audioManager } from '../core/CoreManagerAudio';
import { LayerDepths } from '../constants/enums';
import { Entity } from '../entities/base/Entity';

/**
 * Action to execute when a trigger fires
 */
export interface TriggerAction {
  type: 'destroy' | 'spawn' | 'setAnimation' | 'playAudio';
  params?: {
    object?: string;
    animation?: string;
    audioFile?: string;
    layer?: string;
    imagePoint?: string;
  };
}

/**
 * Handler for animation finished events
 */
export interface AnimationFinishedHandler {
  entityType: string;
  animation: string;
  actions: TriggerAction[];
}

/**
 * Explosion effect configuration
 */
export interface ExplosionEffect {
  onHit?: string;
  onDeath?: string;
  layer: string;
}

/**
 * Animation triggers data structure
 */
export interface AnimationTriggersData {
  _meta: {
    version: string;
    extractedAt: string;
    sources: string[];
  };
  animationFinishedHandlers: AnimationFinishedHandler[];
  collisionSpawnEffects: Array<{
    source: string;
    target: string;
    spawnObject: string;
    layer: string;
    conditions?: string[];
  }>;
  explosionEffects: Record<string, ExplosionEffect>;
  anyAnimationFinished: string[];
}

/**
 * AnimationTriggerSystem - Manages animation triggers extracted from C3
 *
 * Data files:
 * - animation-triggers-c3data.json (base, auto-generated)
 * - animation-triggers-overrides.json (manual adjustments)
 *
 * Features:
 * - Loads and merges c3data + overrides at runtime
 * - Provides explosion effects mapping for projectiles
 * - Can register entities for animation complete handlers
 * - Auto-destroys effect sprites on animation complete
 *
 * Documentation: See docs/animation_triggers.md for system overview
 */
export class AnimationTriggerSystem {
  private scene: Phaser.Scene;
  private data: AnimationTriggersData | null = null;
  private loaded: boolean = false;

  // Cache keys
  static readonly C3DATA_KEY = 'animation-triggers-c3data';
  static readonly OVERRIDES_KEY = 'animation-triggers-overrides';

  // Cache for quick lookups
  private handlersByEntity: Map<string, AnimationFinishedHandler[]> = new Map();
  private autoDestroyEntities: Set<string> = new Set();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Preload the animation triggers JSONs (c3data + overrides)
   */
  preload(): void {
    this.scene.load.json(AnimationTriggerSystem.C3DATA_KEY, '/assets/data/animation-triggers-c3data.json');
    this.scene.load.json(AnimationTriggerSystem.OVERRIDES_KEY, '/assets/data/animation-triggers-overrides.json');
  }

  /**
   * Load data from cache and merge c3data + overrides
   */
  loadFromCache(c3dataKey: string = AnimationTriggerSystem.C3DATA_KEY): void {
    const c3data = this.scene.cache.json.get(c3dataKey) as AnimationTriggersData | undefined;
    if (!c3data) {
      console.error(`[AnimationTriggerSystem] No c3data found in cache for key: ${c3dataKey}`);
      return;
    }

    // Clone c3data as base
    this.data = JSON.parse(JSON.stringify(c3data)) as AnimationTriggersData;

    // Load and merge overrides
    this.loadOverrides();

    this.loaded = true;
    this.buildCaches();
  }

  /**
   * Load and merge overrides into the base data
   */
  loadOverrides(key: string = AnimationTriggerSystem.OVERRIDES_KEY): void {
    if (!this.data) {
      console.error('[AnimationTriggerSystem] Cannot load overrides: base data not loaded');
      return;
    }

    const overrides = this.scene.cache.json.get(key) as Partial<AnimationTriggersData> | undefined;
    if (!overrides) {
      return; // No overrides, that's fine
    }

    // Merge animationFinishedHandlers (append)
    if (overrides.animationFinishedHandlers?.length) {
      this.data.animationFinishedHandlers.push(...overrides.animationFinishedHandlers);
    }

    // Merge collisionSpawnEffects (append)
    if (overrides.collisionSpawnEffects?.length) {
      this.data.collisionSpawnEffects.push(...overrides.collisionSpawnEffects);
    }

    // Merge explosionEffects (overwrite per key)
    if (overrides.explosionEffects) {
      for (const [entityType, effect] of Object.entries(overrides.explosionEffects)) {
        this.data.explosionEffects[entityType] = effect;
      }
    }

    // Merge anyAnimationFinished (append unique)
    if (overrides.anyAnimationFinished?.length) {
      const existing = new Set(this.data.anyAnimationFinished);
      for (const entityType of overrides.anyAnimationFinished) {
        if (!existing.has(entityType)) {
          this.data.anyAnimationFinished.push(entityType);
        }
      }
    }
  }

  /**
   * Build lookup caches for performance
   */
  private buildCaches(): void {
    if (!this.data) return;

    // Group handlers by entity type
    for (const handler of this.data.animationFinishedHandlers) {
      const key = handler.entityType.toLowerCase();
      const existing = this.handlersByEntity.get(key) || [];
      existing.push(handler);
      this.handlersByEntity.set(key, existing);
    }

    // Build auto-destroy set
    for (const entityType of this.data.anyAnimationFinished) {
      this.autoDestroyEntities.add(entityType.toLowerCase());
    }
  }

  /**
   * Check if data is loaded
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Get explosion effect for an entity (projectile type)
   */
  getExplosionEffect(entityType: string): ExplosionEffect | undefined {
    if (!this.data) return undefined;
    return Entity.findInObject(this.data.explosionEffects, entityType);
  }

  /**
   * Get onHit explosion sprite name for a projectile type
   */
  getHitExplosion(projectileType: string): string | undefined {
    return this.getExplosionEffect(projectileType)?.onHit;
  }

  /**
   * Get onDeath explosion sprite name for a projectile type
   */
  getDeathExplosion(projectileType: string): string | undefined {
    return this.getExplosionEffect(projectileType)?.onDeath;
  }

  /**
   * Get layer for explosion effect
   */
  getExplosionLayer(projectileType: string): string {
    const effect = this.getExplosionEffect(projectileType);
    if (!effect) {
      console.warn(`[AnimationTriggerSystem] No explosion effect for projectile: ${projectileType}, using Floor layer`);
      return 'Floor';
    }
    return effect.layer;
  }

  /**
   * Check if an entity type should auto-destroy on any animation finish
   */
  shouldAutoDestroy(entityType: string): boolean {
    return this.autoDestroyEntities.has(entityType.toLowerCase());
  }

  /**
   * Get animation finished handlers for an entity type
   */
  getHandlers(entityType: string): AnimationFinishedHandler[] {
    return this.handlersByEntity.get(entityType.toLowerCase()) || [];
  }

  /**
   * Get handler for a specific entity type and animation
   */
  getHandler(entityType: string, animationName: string): AnimationFinishedHandler | undefined {
    const handlers = this.getHandlers(entityType);
    return handlers.find(h => Entity.matches(h.animation, animationName));
  }

  /**
   * Register an entity sprite for animation triggers
   * This sets up the animationcomplete listener
   */
  registerEntity(sprite: Phaser.GameObjects.Sprite, entityType: string): void {
    const handlers = this.getHandlers(entityType);
    const autoDestroy = this.shouldAutoDestroy(entityType);

    if (handlers.length === 0 && !autoDestroy) {
      return; // No triggers for this entity
    }

    // Remove any existing listener to avoid duplicates
    sprite.off('animationcomplete');

    sprite.on('animationcomplete', (anim: Phaser.Animations.Animation) => {
      // Check for specific animation handlers
      const animName = this.extractAnimationName(anim.key);
      const handler = handlers.find(h => Entity.matches(h.animation, animName));

      if (handler) {
        this.executeActions(sprite, handler.actions);
      } else if (autoDestroy) {
        // Auto-destroy effect sprites
        sprite.destroy();
      }
    });
  }

  /**
   * Extract animation name from key (e.g., "laser_Guarda" -> "Guarda")
   */
  private extractAnimationName(key: string): string {
    if (key.includes('_')) {
      return key.split('_').slice(1).join('_');
    }
    return key;
  }

  /**
   * Execute trigger actions
   */
  private executeActions(sprite: Phaser.GameObjects.Sprite, actions: TriggerAction[]): void {
    for (const action of actions) {
      switch (action.type) {
        case 'destroy':
          sprite.destroy();
          break;

        case 'spawn':
          if (action.params?.object) {
            this.spawnEffect(sprite, action.params.object, action.params.layer);
          }
          break;

        case 'setAnimation':
          if (action.params?.animation) {
            sprite.play(action.params.animation, true);
          }
          break;

        case 'playAudio':
          if (action.params?.audioFile) {
            audioManager.playSfx(action.params.audioFile, 'entity', this.scene);
          }
          break;
      }
    }
  }

  /**
   * Spawn an effect at sprite position (for auto-destroy entities only)
   * Note: Explosion effects are now handled by EffectsSystem with data-driven config
   */
  spawnEffect(
    source: Phaser.GameObjects.Sprite | { x: number; y: number },
    effectType: string,
    layer?: string
  ): Phaser.GameObjects.Sprite | null {
    // Check if the texture exists
    if (!this.scene.textures.exists(effectType)) {
      console.warn(`[AnimationTriggerSystem] Missing texture for effect: ${effectType}`);
      return null;
    }

    // Create effect sprite
    const effect = this.scene.add.sprite(source.x, source.y, effectType);

    // Set depth based on layer (OverImages for explosions)
    const depth = this.getLayerDepth(layer || 'OverImages');
    effect.setDepth(depth);

    // Register for auto-destroy if applicable
    if (this.shouldAutoDestroy(effectType)) {
      this.registerEntity(effect, effectType);
    }

    // Try to play default animation
    const animKey = `${effectType}_Default`;
    if (this.scene.anims.exists(animKey)) {
      effect.play(animKey);
    } else {
      // Try without prefix
      if (this.scene.anims.exists(effectType)) {
        effect.play(effectType);
      }
    }

    return effect;
  }

  /**
   * Get depth value for C3 layer name
   */
  private getLayerDepth(layerName: string): number {
    // Layer depth mapping (higher = in front)
    const layerDepthMap: Record<string, number> = {
      'Background': LayerDepths.Background,
      'Floor': LayerDepths.Floor,
      'Destructables': LayerDepths.Destructables,
      'Abducteables': LayerDepths.Abducteables,
      'Units': LayerDepths.Entities,
      'OverImages': LayerDepths.Effects,
      'Projectiles': LayerDepths.Projectiles,
      'HUD': LayerDepths.HUD,
    };
    const depth = layerDepthMap[layerName];
    if (depth === undefined) {
      console.warn(`[AnimationTriggerSystem] Unknown layer: ${layerName}, using Floor`);
      return LayerDepths.Floor;
    }
    return depth;
  }

  /**
   * Debug: list all explosion effects
   */
  debugExplosionEffects(): void {
    if (!this.data) {
      console.log('[AnimationTriggerSystem] No data loaded');
      return;
    }

    console.log('[AnimationTriggerSystem] Explosion Effects:');
    for (const [entity, effect] of Object.entries(this.data.explosionEffects)) {
      console.log(`  ${entity}: onHit=${effect.onHit || '-'}, onDeath=${effect.onDeath || '-'}, layer=${effect.layer}`);
    }

    console.log('\n[AnimationTriggerSystem] Auto-destroy entities:');
    console.log(`  ${Array.from(this.autoDestroyEntities).join(', ')}`);
  }
}
